/**
 * Cola de reintento.
 *
 * El coordinador eligió cola ligera: la entrevista se captura en el
 * dispositivo, se encola al terminar y se envía cuando hay red. No hay
 * sincronización bidireccional.
 *
 * Reglas que hacen que no se pierda trabajo de campo:
 *
 *  1. IDEMPOTENCIA. Cada entrevista lleva un `clientUuid` generado en el
 *     dispositivo, que es la llave con la que el servidor la reconoce. Un
 *     reenvío tras un corte de red devuelve `duplicate`, no un duplicado real.
 *  2. UN FALLO DE RED NO ES UN RECHAZO. Sin red el elemento vuelve a `pending`
 *     y se reintenta. Sólo un 4xx del servidor lo marca `rejected`, porque
 *     reintentarlo daría el mismo resultado.
 *  3. NADA SE BORRA SOLO. Lo aceptado se conserva un tiempo para que el
 *     encuestador vea que llegó, y lo rechazado se queda hasta que el
 *     supervisor lo revise.
 *  4. LOTES ACOTADOS. Se envían de a pocas para que un lote grande no falle
 *     completo por una sola entrevista con problema y para no agotar el tiempo
 *     de espera en una conexión mala.
 */

import { api, session, ApiError, NetworkError } from '../api/client';
import { sendPending } from '../telemetry/dataUsage';
import { db, type QueueItem, type QueueItemKind } from './db';

/** Entrevistas por lote. Con conexión celular inestable, pocas y seguidas. */
const BATCH_SIZE = 5;

/** Tope de intentos automáticos antes de pedir intervención. */
const MAX_AUTO_ATTEMPTS = 8;

/** Lo aceptado se conserva este tiempo para que el encuestador lo vea. */
const SYNCED_RETENTION_MS = 1000 * 60 * 60 * 24 * 3;

export interface SyncOutcome {
  attempted: number;
  accepted: number;
  duplicates: number;
  rejected: number;
  networkFailure: boolean;
  messages: string[];
}

/* -------------------------------------------------------------------------- */
/*                                  Encolar                                   */
/* -------------------------------------------------------------------------- */

export async function enqueue(
  id: string,
  kind: QueueItemKind,
  payload: unknown,
  summary: string,
): Promise<void> {
  const existing = await db.queue.get(id);
  // Si ya se sincronizó, no se vuelve a encolar: evita reenviar algo que el
  // servidor ya aceptó cuando el encuestador toca "finalizar" dos veces.
  if (existing?.status === 'synced') return;

  await db.queue.put({
    id,
    kind,
    payload,
    status: 'pending',
    attempts: existing?.attempts ?? 0,
    lastAttemptAt: existing?.lastAttemptAt ?? null,
    lastError: null,
    createdAt: existing?.createdAt ?? Date.now(),
    syncedAt: null,
    summary,
  });
}

/* -------------------------------------------------------------------------- */
/*                                Sincronizar                                 */
/* -------------------------------------------------------------------------- */

let syncInFlight = false;

export async function syncNow(): Promise<SyncOutcome> {
  const outcome: SyncOutcome = {
    attempted: 0,
    accepted: 0,
    duplicates: 0,
    rejected: 0,
    networkFailure: false,
    messages: [],
  };

  // Una sola sincronización a la vez: dos en paralelo enviarían la misma
  // entrevista dos veces y, aunque el servidor es idempotente, confundirían el
  // conteo que ve el encuestador.
  if (syncInFlight) {
    outcome.messages.push('Ya hay una sincronización en curso.');
    return outcome;
  }
  if (!navigator.onLine) {
    outcome.networkFailure = true;
    outcome.messages.push('Sin conexión. Se reintentará automáticamente.');
    return outcome;
  }

  syncInFlight = true;
  try {
    while (true) {
      const batch = await db.queue
        .where('status')
        .equals('pending')
        .filter((item) => item.attempts < MAX_AUTO_ATTEMPTS)
        .limit(BATCH_SIZE)
        .toArray();

      if (batch.length === 0) break;

      await db.queue.bulkPut(
        batch.map((item) => ({ ...item, status: 'sending' as const, lastAttemptAt: Date.now() })),
      );

      const interviews = batch.filter((i) => i.kind === 'interview').map((i) => i.payload);
      const households = batch.filter((i) => i.kind === 'household').map((i) => i.payload);
      outcome.attempted += batch.length;

      try {
        const result = await api.sync({ interviews, households });

        const byUuid = new Map<string, { outcome: string; message?: string }>();
        for (const row of result.interviews) byUuid.set(row.clientUuid, row);
        for (const row of result.households) byUuid.set(row.clientUuid, row);

        for (const item of batch) {
          const row = byUuid.get(item.id);
          if (!row) {
            // El servidor no se pronunció: se devuelve a pendiente en lugar de
            // darla por buena. Dar por enviada una entrevista que no llegó es
            // el peor fallo posible aquí.
            await db.queue.update(item.id, {
              status: 'pending',
              attempts: item.attempts + 1,
              lastError: 'El servidor no confirmó este elemento.',
            });
            continue;
          }

          if (row.outcome === 'accepted' || row.outcome === 'duplicate') {
            await db.queue.update(item.id, {
              status: 'synced',
              syncedAt: Date.now(),
              lastError: null,
              attempts: item.attempts + 1,
            });
            if (row.outcome === 'accepted') outcome.accepted++;
            else outcome.duplicates++;

            // El borrador ya está en el servidor; se libera el espacio local.
            if (item.kind === 'interview') {
              await db.drafts.delete(item.id);
            }
          } else {
            await db.queue.update(item.id, {
              status: 'rejected',
              attempts: item.attempts + 1,
              lastError: row.message ?? 'El servidor rechazó el registro.',
            });
            outcome.rejected++;
            if (row.message) outcome.messages.push(`${item.summary}: ${row.message}`);
          }
        }
      } catch (error) {
        const isNetwork = error instanceof NetworkError;
        const isPermanent = error instanceof ApiError && error.isPermanent;

        for (const item of batch) {
          await db.queue.update(item.id, {
            // Un 4xx del lote completo (por ejemplo el token caducado sin
            // posibilidad de renovar) no debe marcar las entrevistas como
            // rechazadas: el problema es de la sesión, no de los datos.
            status: isPermanent && error instanceof ApiError && error.status !== 401 ? 'rejected' : 'pending',
            attempts: item.attempts + 1,
            lastError:
              error instanceof Error ? error.message : 'Error desconocido al sincronizar.',
          });
        }

        if (isNetwork) {
          outcome.networkFailure = true;
          outcome.messages.push('Se perdió la conexión durante el envío. Se reintentará.');
        } else if (error instanceof ApiError) {
          outcome.messages.push(error.message);
          if (error.isPermanent && error.status !== 401) {
            outcome.rejected += batch.length;
          }
        }
        break;
      }
    }

    await pruneSynced();
  } finally {
    syncInFlight = false;
  }

  /**
   * El consumo de datos viaja DESPUÉS y en su propio intento.
   *
   * Es telemetría: si falla, no puede arrastrar a las entrevistas, que son lo
   * único irrecuperable. Los tramos quedan en el dispositivo y se reintentan
   * en la siguiente sincronización.
   */
  try {
    await sendPending(session.deviceId);
  } catch {
    /* se reintenta después; no se pierde nada */
  }

  return outcome;
}

/** Reintenta manualmente lo que agotó los intentos o fue rechazado. */
export async function retryFailed(): Promise<number> {
  const failed = await db.queue
    .filter((item) => item.status === 'rejected' || item.attempts >= MAX_AUTO_ATTEMPTS)
    .toArray();
  await db.queue.bulkPut(
    failed.map((item) => ({ ...item, status: 'pending' as const, attempts: 0, lastError: null })),
  );
  return failed.length;
}

async function pruneSynced(): Promise<void> {
  const cutoff = Date.now() - SYNCED_RETENTION_MS;
  const stale = await db.queue
    .where('status')
    .equals('synced')
    .filter((item) => (item.syncedAt ?? 0) < cutoff)
    .primaryKeys();
  if (stale.length > 0) await db.queue.bulkDelete(stale);
}

export async function queueSnapshot(): Promise<{
  pending: QueueItem[];
  sending: QueueItem[];
  rejected: QueueItem[];
  synced: QueueItem[];
  exhausted: QueueItem[];
}> {
  const all = await db.queue.orderBy('createdAt').toArray();
  return {
    pending: all.filter((i) => i.status === 'pending' && i.attempts < MAX_AUTO_ATTEMPTS),
    sending: all.filter((i) => i.status === 'sending'),
    rejected: all.filter((i) => i.status === 'rejected'),
    synced: all.filter((i) => i.status === 'synced'),
    exhausted: all.filter((i) => i.status === 'pending' && i.attempts >= MAX_AUTO_ATTEMPTS),
  };
}

/* -------------------------------------------------------------------------- */
/*                           Sincronización automática                        */
/* -------------------------------------------------------------------------- */

let autoSyncTimer: number | null = null;

/**
 * Arranca la sincronización automática.
 *
 * Se dispara al recuperar conexión y cada pocos minutos. El intervalo es
 * holgado a propósito: en campo la batería importa, y reintentar cada 30
 * segundos sin señal la consume sin ganar nada.
 */
export function startAutoSync(intervalMs = 1000 * 60 * 3): () => void {
  const run = () => {
    void syncNow();
  };

  const onOnline = () => {
    // Pequeña espera: al recuperar señal la conexión suele tardar un momento en
    // ser utilizable, y un envío inmediato fallaría y gastaría un intento.
    window.setTimeout(run, 2500);
  };

  window.addEventListener('online', onOnline);
  autoSyncTimer = window.setInterval(run, intervalMs);
  run();

  return () => {
    window.removeEventListener('online', onOnline);
    if (autoSyncTimer !== null) window.clearInterval(autoSyncTimer);
    autoSyncTimer = null;
  };
}
